import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

const STORAGE_KEY_EXPANDED = 'statscout_sidebar_expanded'
const STORAGE_KEY_LEAGUES_EXPANDED = 'statscout_sidebar_leagues_expanded'

interface SidebarContextType {
  // Desktop sidebar expanded state (persisted)
  isExpanded: boolean
  setIsExpanded: (expanded: boolean) => void
  toggleExpanded: () => void

  // Mobile drawer state
  isMobileMenuOpen: boolean
  setMobileMenuOpen: (open: boolean) => void
  toggleMobileMenu: () => void
  closeMobileMenu: () => void

  // League accordion state (persisted)
  isLeaguesExpanded: boolean
  setLeaguesExpanded: (expanded: boolean) => void
  toggleLeaguesExpanded: () => void

  // Convenience
  isCollapsed: boolean
}

const SidebarContext = createContext<SidebarContextType | null>(null)

interface SidebarProviderProps {
  children: ReactNode
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  // Initialize from localStorage or default to expanded
  const [isExpanded, setIsExpandedState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_EXPANDED)
      return stored !== null ? stored === 'true' : true
    }
    return true
  })

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)

  const [isLeaguesExpanded, setLeaguesExpandedState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_LEAGUES_EXPANDED)
      return stored !== null ? stored === 'true' : true
    }
    return true
  })

  // Persist expanded state
  const setIsExpanded = useCallback((expanded: boolean) => {
    setIsExpandedState(expanded)
    localStorage.setItem(STORAGE_KEY_EXPANDED, String(expanded))
  }, [])

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded)
  }, [isExpanded, setIsExpanded])

  // Mobile menu handlers
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev)
  }, [])

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false)
  }, [])

  // Leagues accordion handlers
  const setLeaguesExpanded = useCallback((expanded: boolean) => {
    setLeaguesExpandedState(expanded)
    localStorage.setItem(STORAGE_KEY_LEAGUES_EXPANDED, String(expanded))
  }, [])

  const toggleLeaguesExpanded = useCallback(() => {
    setLeaguesExpanded(!isLeaguesExpanded)
  }, [isLeaguesExpanded, setLeaguesExpanded])

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isMobileMenuOpen, closeMobileMenu])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  const value: SidebarContextType = {
    isExpanded,
    setIsExpanded,
    toggleExpanded,
    isMobileMenuOpen,
    setMobileMenuOpen,
    toggleMobileMenu,
    closeMobileMenu,
    isLeaguesExpanded,
    setLeaguesExpanded,
    toggleLeaguesExpanded,
    isCollapsed: !isExpanded,
  }

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

export function useSidebar(): SidebarContextType {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
